import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import {
	Select,
	Button,
	Dropdown,
	Table,
	Menu,
	Divider,
	Modal,
	Icon,
	message,
	Typography,
	Input,
	Breadcrumb,
	Tag,
	Radio,
	Avatar,
} from 'antd';

import Meta from '../../../../components/Meta';
import Fallback from '../../../../components/Fallback';
import Form from '../../../../components/Form';

import PageSizeHandler from '../../../../components/PageSizeHandle';
import { CasterOption, Container, TableHeader } from './styles';

import UsersAPI from '../../../../services/sdks/user';
import GenresAPI from '../../../../services/sdks/genres';
import ProgramsAPI from '../../../../services/sdks/programs';
import CastersApi from '../../../../services/sdks/caster';

import { FiEdit2, FiPlusCircle, FiSave, FiXCircle } from 'react-icons/fi';
import { resolveFileSrc } from '../../../../helpers/fileSrcResolver';
import env from '../../../../config/env';
import AiModelsAPI from '../../../../services/sdks/ai-models';

const RuleInput = ({ ruleKey, label, accountInfos, handleChangeAccountInfo, placeholderText }) => {
	const [inputValue, setInputValue] = React.useState('');

	const handleAddRule = (e) => {
		e.preventDefault();
		const value = inputValue.trim();
		if (!value) return;

		const updated = {
			...accountInfos.rules,
			[ruleKey]: [...(accountInfos.rules?.[ruleKey] || []), value],
		};

		handleChangeAccountInfo('rules', updated);

		setInputValue('');
	};

	const handleRemoveRule = (ruleToRemove) => {
		const updated = {
			...accountInfos.rules,
			[ruleKey]: accountInfos.rules[ruleKey].filter((rule) => rule !== ruleToRemove),
		};

		handleChangeAccountInfo('rules', updated);
	};

	return (
		<Form.Item
			label={label}
			labelCol={{ span: 24 }}
			wrapperCol={{ span: 24 }}
			style={{ marginBottom: 0 }}>
			<>
				<Input.TextArea
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					placeholder={placeholderText}
					autoSize={{ minRows: 1, maxRows: 3 }}
					onPressEnter={handleAddRule}
				/>
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						gap: 8,
						marginTop: 8,
					}}>
					{(accountInfos?.rules?.[ruleKey] || []).map((rule, index) => (
						<Tag
							key={`${ruleKey}-${rule}-${index}`}
							closable
							onClose={(e) => {
								e.preventDefault();
								handleRemoveRule(rule);
							}}
							style={{
								whiteSpace: 'normal',
								lineHeight: 1.5,
								padding: '8px 12px',
								fontSize: 14,
								background: '#fafafa',
								border: '1px solid #d9d9d9',
							}}>
							{rule}
						</Tag>
					))}
				</div>
			</>
		</Form.Item>
	);
};

const ManagePrograms = () => {
	const [fallback, setFallback] = useState({ initialData: true });
	const [visibleModals, setVisibleModals] = useState({ editProgram: false });
	const [handlingProgram, setHandlingProgram] = useState(null);

	const [users, setUsers] = useState([]);
	const [casters, setCasters] = useState([]);
	const [genres, setGenres] = useState([]);
	const [programs, setPrograms] = useState([]);
	const [selectedPrograms, setSelectedPrograms] = useState([]);

	const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: null });
	const [filters, setFilters] = useState({ name: '', userId: undefined, isDeleted: false });

	const [aiModels, setAiModels] = useState([]);
	const [selectedAiModels, setSelectedAiModels] = useState([]);

	const getAiModels = async (userId) => {
		setAiModels([]);
		const { data } = await AiModelsAPI.listAiModels({ userId, pageSize: 9999 });
		if (data.total) {
			const aiModelsValues = data.aiModels.map((aiModel) => {
				return { value: aiModel._id, label: aiModel.title };
			});
			setAiModels(aiModelsValues);
		} else {
			setAiModels([]);
		}
	};

	const getSelectedAiModels = async (programId) => {
		setSelectedAiModels([]);
		const { data } = await ProgramsAPI.show(programId);
		const { program } = data;
		if (program?.aiModels.length) {
			const selectedIds = program.aiModels.map((aiModel) => aiModel._id);
			setSelectedAiModels(selectedIds);
		}
	};

	useEffect(() => {
		if (handlingProgram) {
			getAiModels(handlingProgram.userId._id);
			getSelectedAiModels(handlingProgram._id);
		}
	}, [handlingProgram]);

	const columns = [
		{
			title: 'Nome',
			key: 'name',
			render: (program) => program?.name,
		},
		{
			title: 'Rádio',
			key: 'radio',
			dataIndex: 'userId',
			render: (user) => (
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<span>{user?.radioName}</span>
					<i style={{ fontSize: 11 }}>
						<i>{user?.city}</i>, <strong>{user?.state}</strong>
					</i>
				</div>
			),
		},
		{
			title: 'Edição',
			key: 'editable',
			dataIndex: 'isEditable',
			align: 'center',
			render: (isEditable) =>
				isEditable ? <Tag color='green'>Editável</Tag> : <Tag color='orange'>Não editável</Tag>,
		},
		{
			title: 'Status de Exclusão',
			key: 'isDeleted',
			dataIndex: 'isDeleted',
			align: 'center',
			render: (isDeleted) =>
				isDeleted ? <Tag color='red'>Excluído</Tag> : <Tag color='green'>Não excluído</Tag>,
		},
		{
			key: 'id',
			title: 'ID',
			align: 'center',
			render: ({ _id }) => (
				<Typography.Text title={_id} copyable={{ text: _id }}>{`${_id.slice(
					0,
					12
				)}...`}</Typography.Text>
			),
		},
		{
			title: 'Ações',
			align: 'right',
			key: 'actions',
			render: (program) => (
				<Dropdown
					placement='bottomRight'
					overlay={
						<Menu>
							<Menu.Item
								onClick={() => {
									const c = program?.caster;
									const casterId = typeof c === 'string' ? c : c?._id;
									setHandlingProgram({
										...program,
										caster: casterId,
										theme: program.theme || '',
										audience: program.audience || '',
									});
									setVisibleModals({ ...visibleModals, editProgram: true });
								}}>
								<Icon type='edit' /> Editar
							</Menu.Item>

							<Menu.Divider />

							<Menu.Item
								className='ant-dropdown-menu-item-danger'
								disabled={!program?.isDeleted}
								onClick={() => {
									Modal.confirm({
										title: 'Reverter exclusão?',
										icon: 'exclamation-circle',
										content: 'O programa será adicionado à grade do usuário, deseja continuar?',
										onOk: () => handleRevertExclusion(program),
										okText: 'Reverter',
										okButtonProps: {
											icon: 'retweet',
											type: 'primary',
										},
										cancelText: 'Cancelar',
										cancelButtonProps: {
											icon: 'close-circle',
										},
									});
								}}>
								<Icon type='retweet' /> Reverter exclusão
							</Menu.Item>
						</Menu>
					}>
					<Icon style={{ cursor: 'pointer', fontSize: 20, marginRight: 12 }} type='more' />
				</Dropdown>
			),
		},
	];

	const handleUpdateProgram = useCallback(async (payload, selectedAiModelsProp) => {
		try {
			console.log('>>>>> ', payload);

			if (!('dayPeriod' in payload) || payload.dayPerio === undefined) {
				payload['dayPeriod'] = null;
			}

			const validationSchema = Yup.object().shape({
				name: Yup.string().required('Informe o nome'),
				description: Yup.string().required('Informe a descrição'),
				theme: Yup.string().required('Informe o tema'),
				audience: Yup.string().required('Informe a audiência'),
				filenameTemplate: Yup.string().required('Informe o padrão do nome de arquivo'),
				dayPeriod: Yup.string()
					.nullable()
					.notRequired()
					.oneOf(
						[
							'MORNING',
							'LATE_MORNING',
							'AFTERNOON',
							'LATE_AFTERNOON',
							'NIGHT',
							'LATE_NIGHT',
							'DAWN',
							null,
						],
						'dayPeriod inválido'
					),
			});

			await validationSchema.validate(payload);
			setFallback((prev) => ({ ...prev, editing: true }));

			const response = await ProgramsAPI.update(payload?._id, {
				...payload,
				aiModelIds: selectedAiModelsProp,
			});
			const updatedProgram = response.data.program;

			setPrograms((prev) =>
				prev.map((program) => {
					if (program?._id === updatedProgram?._id) {
						return updatedProgram;
					}

					return program;
				})
			);

			setVisibleModals((prev) => ({ ...prev, editProgram: false }));
			setHandlingProgram(null);
		} catch (error) {
			if (error instanceof Yup.ValidationError) {
				return message.error(error.message);
			}

			console.error(error);
			message.error('Houve um erro ao editar o programa');
		} finally {
			setFallback((prev) => ({ ...prev, editing: false }));
		}
	}, []);

	const handleRevertExclusion = useCallback(
		async (program) => {
			try {
				await handleUpdateProgram({
					...program,
					deleteRequested: false,
					deleteReason: null,
					isDeleted: false,
				});

				message.success('A exclusão foi revertida e o programa retornou a grade do usuário');
			} catch (error) {
				console.error(error);
				message.error('Houve um erro, tente novamente');
			}
		},
		[handleUpdateProgram]
	);

	useEffect(() => {
		async function getUsers() {
			const res = await UsersAPI.index('active=true&pageNumber=0&pageSize=99999');
			return res.data.users;
		}

		async function getCasters() {
			const res = await CastersApi.index(`active=true&page=0&limit=9999`);
			return res.data.casters;
		}

		async function getGenres() {
			const res = await GenresAPI.index();
			return res.data.genres;
		}

		const fetchInitialData = async () => {
			try {
				const [users, casters, genres] = await Promise.all([getUsers(), getCasters(), getGenres()]);

				setUsers(users);
				setCasters(casters);
				setGenres(genres);
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os usuários');
			} finally {
				setFallback((prev) => ({ ...prev, initialData: false }));
			}
		};

		fetchInitialData();
	}, []);

	useEffect(() => {
		const fetchPrograms = async () => {
			try {
				setFallback((prev) => ({ ...prev, fetchingPrograms: true }));

				const {
					data: { programs, total },
				} = await ProgramsAPI.index({
					name: filters.name,
					isActive: true,
					isDeleted: filters.isDeleted,
					pageNumber: pagination.current - 1,
					pageSize: pagination.pageSize,
					userId: filters?.userId,
				});

				setPrograms(
					programs.map((p) => {
						return {
							...p,
							genreId: p?.genreId?._id ? p?.genreId?._id : undefined,
						};
					})
				);
				setPagination((prev) => ({ ...prev, total }));
			} catch (error) {
				console.error(error);
				message.error('Houve um erro ao buscar os programas');
			} finally {
				setFallback((prev) => ({ ...prev, fetchingPrograms: false }));
			}
		};

		fetchPrograms();
	}, [filters, pagination.current, pagination.pageSize]); //eslint-disable-line

	useEffect(() => {
		/**
		 * Disparado quando alguma filtragem é realizada
		 * Reseta a página atual para 1
		 * */
		setPagination((prev) => ({ ...prev, current: 1 }));
	}, [filters]);

	const userCasters = useMemo(() => {
		if (!handlingProgram?.userId?._id) {
			return [];
		}

		return casters.filter((c) => {
			return c.userId === env.talkId || c.userId === handlingProgram?.userId?._id;
		});
	}, [casters, handlingProgram?.userId?._id]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando' message='Por favor, aguarde...' />;
	}

	return (
		<>
			<Meta title='Gerenciar programas' />

			<Container>
				<Breadcrumb
					style={{ marginBottom: 12 }}
					separator='>'
					routes={[
						{ breadcrumbName: 'PAINEL ADMINISTRATIVO' },
						{ breadcrumbName: 'CONTEÚDOS' },
						{ breadcrumbName: 'GERENCIAR PROGRAMAS' },
					]}
				/>

				<header>
					<Typography.Title level={2}>Gerenciar programas</Typography.Title>
					<Link to='/admin/contents/programs/create-program'>
						<Button type='primary'>
							<FiPlusCircle /> Novo programa
						</Button>
					</Link>
				</header>

				<Form.Container layout='30% 30% 30%'>
					<Form.Item label='Filtrar por usuário'>
						<Select
							showSearch
							optionFilterProp='children'
							placeholder='Selecione o usuário'
							value={filters?.userId}
							onChange={(value) => {
								setFilters({ ...filters, userId: value });
							}}
							filterOption={(input, { props: { _search } }) => {
								const regex = new RegExp(input, 'i');

								return _search.match(regex);
							}}>
							{users.map((user) => {
								const { radioName, city, state, email } = user;

								return (
									<Select.Option
										key={user?._id}
										value={user?._id}
										_search={`${radioName}${city}${state}${email}`}>
										{radioName} - {city}/{state} ({email})
									</Select.Option>
								);
							})}
						</Select>
					</Form.Item>

					<Form.Item label='Buscar por nome' help='Forneça no mínimo 3 caracteres'>
						<Input.Search
							allowClear
							onChange={({ target: { value } }) => !value && setFilters({ ...filters, name: '' })}
							onSearch={(value) => setFilters({ ...filters, name: value })}
							placeholder='Informe o nome do programa'
						/>
					</Form.Item>

					<Form.Item label='Filtrar por status de exclusão'>
						<Radio.Group
							size='small'
							buttonStyle='solid'
							value={filters?.isDeleted}
							onChange={({ target: { value } }) => setFilters({ ...filters, isDeleted: value })}>
							<Radio.Button value={false}>Não Excluídos</Radio.Button>
							<Radio.Button value={true}>Somente Excluídos</Radio.Button>
						</Radio.Group>
					</Form.Item>
				</Form.Container>

				<Divider />

				<TableHeader>
					<div className='actions'>
						<span>
							Quantidade: <strong>{pagination?.total}</strong>
						</span>
					</div>

					<PageSizeHandler pagination={pagination} setPagination={setPagination} />
				</TableHeader>

				<Table
					rowKey='_id'
					size='middle'
					columns={columns}
					dataSource={programs}
					loading={fallback?.fetchingPrograms}
					style={{ border: 'none' }}
					pagination={{
						...pagination,
						size: 'large',
						onChange: (current) => setPagination({ ...pagination, current }),
					}}
					rowSelection={{
						selectedRowKeys: selectedPrograms,
						onChange: (_, selectedRows) => {
							setSelectedPrograms(selectedRows.map(({ _id }) => _id));
						},
					}}
				/>

				<Modal
					visible={visibleModals?.editProgram}
					title={
						<>
							<FiEdit2 /> Editar programa
						</>
					}
					okText={
						<>
							<FiSave /> Salvar alterações
						</>
					}
					cancelText={
						<>
							<FiXCircle /> Cancelar
						</>
					}
					closable={!fallback?.editing}
					okButtonProps={{ loading: fallback?.editing }}
					cancelButtonProps={{ disabled: fallback?.editing }}
					onOk={() => handleUpdateProgram(handlingProgram, selectedAiModels)}
					width='80vw'
					onCancel={() => {
						setVisibleModals({ ...visibleModals, editProgram: false });
						setHandlingProgram(null);
					}}>
					<Form.Container layout={'1fr 1fr 1fr'}>
						<Form.Item label='Nome' size={1}>
							<Input
								style={{ height: '40px' }}
								size='large'
								placeholder='Nome do programa'
								value={handlingProgram?.name}
								onChange={({ target: { value } }) => {
									setHandlingProgram({ ...handlingProgram, name: value });
								}}
							/>
						</Form.Item>

						<Form.Item label='Locutor (Opcional)' size={1}>
							<Select
								showSearch
								size='large'
								optionFilterProp='children'
								placeholder='Selecione o locutor'
								value={handlingProgram?.caster || undefined}
								onChange={(value) => {
									setHandlingProgram({ ...handlingProgram, caster: value });
								}}>
								{userCasters.map((caster) => (
									<Select.Option value={caster?._id} key={caster?._id}>
										<CasterOption>
											<div>
												<Avatar
													style={{ background: 'var(--primary)' }}
													src={resolveFileSrc({
														fileName: caster?.profilePic,
														folder: ['profile_pics'],
														bucket: env.miscS3Bucket,
													})}>
													{caster?.name[0] || '-'} {caster?.surname[0] || '-'}
												</Avatar>
												<Typography.Text className=''>
													{caster?.name} {caster?.surname}
												</Typography.Text>
											</div>
											<small>
												{caster.userId === env.talkId ? 'LOCUTOR TALK' : 'LOCUTOR DA RÁDIO'}
											</small>
										</CasterOption>
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item label='Modelos de IA' size={1}>
							<Select
								mode='multiple'
								allowClear
								showSearch
								style={{ width: '100%' }}
								placeholder='Digite para buscar ou selecione'
								onChange={setSelectedAiModels}
								value={selectedAiModels}
								// loading={aiModels.length === 0}
								disabled={aiModels.length === 0}
								size='large'
								notFoundContent={
									aiModels.length === 0 ? 'Carregando...' : 'Nenhuma opção encontrada'
								}
								filterOption={(input, option) =>
									option?.props?.children?.toLowerCase()?.includes(input.toLowerCase()) ?? false
								}>
								{aiModels.map((aiModel) => (
									<Select.Option key={aiModel.value} value={aiModel.value}>
										{aiModel.label}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item label='Tema' size={1}>
							<Input
								size='large'
								placeholder='Tema do programa'
								value={handlingProgram?.theme}
								onChange={({ target: { value } }) => {
									setHandlingProgram({ ...handlingProgram, theme: value });
								}}
							/>
						</Form.Item>

						<Form.Item label='Audiência'>
							<Input
								size='large'
								placeholder='Audiência do programa'
								value={handlingProgram?.audience}
								onChange={({ target: { value } }) => {
									setHandlingProgram({ ...handlingProgram, audience: value });
								}}
							/>
						</Form.Item>

						<Form.Item label='Padrão do nome dos arquivos'>
							<Input
								size='large'
								placeholder='Informe o prefixo dos arquivos baixados'
								value={handlingProgram?.filenameTemplate}
								onChange={({ target: { value } }) => {
									setHandlingProgram({ ...handlingProgram, filenameTemplate: value });
								}}
							/>
						</Form.Item>

						<Form.Item label='Gênero'>
							<Select
								showSearch
								size='large'
								optionFilterProp='children'
								placeholder='Selecione o gênero'
								value={handlingProgram?.genreId}
								onChange={(value) => {
									setHandlingProgram({ ...handlingProgram, genreId: value });
								}}>
								{genres.map((genre) => (
									<Select.Option value={genre?._id} key={genre?._id}>
										{genre.name}
									</Select.Option>
								))}
							</Select>
						</Form.Item>

						<Form.Item label='Período do Dia (Opcional)'>
							<Select
								allowClear
								size='large'
								placeholder='Selecione o período do dia do programa (opcional)'
								value={handlingProgram?.dayPeriod ?? undefined}
								onChange={(value) => {
									setHandlingProgram({ ...handlingProgram, dayPeriod: value });
								}}>
								<Select.Option value='MORNING'>Manhã</Select.Option>
								<Select.Option value='LATE_MORNING'>Final da Manhã</Select.Option>
								<Select.Option value='AFTERNOON'>Tarde</Select.Option>
								<Select.Option value='LATE_AFTERNOON'>Final da Tarde</Select.Option>
								<Select.Option value='NIGHT'>Noite</Select.Option>
								<Select.Option value='LATE_NIGHT'>Final da Noite</Select.Option>
								<Select.Option value='DAWN'>Madrugada</Select.Option>
							</Select>
						</Form.Item>

						<Form.Item label='Descrição' size={4}>
							<Input.TextArea
								// size='large'
								// rows={4}
								placeholder='Descrição do programa'
								value={handlingProgram?.description}
								onChange={({ target: { value } }) => {
									setHandlingProgram({ ...handlingProgram, description: value });
								}}
							/>
						</Form.Item>

						<Form.Item optional size={4} label='Segmentos'>
							<Select
								mode='multiple'
								size='large'
								name='segments'
								placeholder='Selecione um ou mais segmentos'
								value={handlingProgram?.segments || []}
								onChange={(value) => setHandlingProgram({ ...handlingProgram, segments: value })}>
								<Select.Option key='POPULAR' value='POPULAR'>
									Popular
								</Select.Option>
								<Select.Option key='JOVEM' value='JOVEM'>
									Jovem
								</Select.Option>
								<Select.Option key='RELIGIOSO' value='RELIGIOSO'>
									Religioso
								</Select.Option>
								<Select.Option key='NEWS' value='NEWS'>
									News / Jornalismo
								</Select.Option>
								<Select.Option key='ESPORTIVA' value='ESPORTIVA'>
									Esportiva
								</Select.Option>
								<Select.Option key='EDUCATIVA' value='EDUCATIVA'>
									Educativa
								</Select.Option>
								<Select.Option key='UNIVERSITÁRIA' value='UNIVERSITÁRIA'>
									Universitária
								</Select.Option>
								<Select.Option key='COMUNITÁRIA' value='COMUNITÁRIA'>
									Comunitária
								</Select.Option>
								<Select.Option key='RETRO' value='RETRO'>
									Retro
								</Select.Option>
								<Select.Option key='REGIONAL' value='REGIONAL'>
									Regional
								</Select.Option>
								<Select.Option key='VARIEDADES' value='VARIEDADES'>
									Variedades
								</Select.Option>
								<Select.Option key='CORPORATIVA' value='CORPORATIVA'>
									Corporativa
								</Select.Option>
								<Select.Option key='GOVERNAMENTAL' value='GOVERNAMENTAL'>
									Governamental
								</Select.Option>
								<Select.Option key='INFANTIL' value='INFANTIL'>
									Infantil
								</Select.Option>
								<Select.Option key='HUMOR' value='HUMOR'>
									Humor
								</Select.Option>
								<Select.Option key='ENTRETENIMENTO' value='ENTRETENIMENTO'>
									Entretenimento
								</Select.Option>
								<Select.Option key='CULTURAL' value='CULTURAL'>
									Cultural
								</Select.Option>
								<Select.Option key='TALK' value='TALK'>
									Talk
								</Select.Option>
							</Select>
						</Form.Item>

						<Form.Item label='Personalidade' style={{ marginTop: 16 }} optional size={4}>
							<Input.TextArea
								placeholder='Descreva a Personalidade'
								value={handlingProgram?.personality}
								onChange={({ target: { value } }) => {
									setHandlingProgram({ ...handlingProgram, personality: value });
								}}
							/>
						</Form.Item>

						<Form.Item label='Sotaque' style={{ marginTop: 16 }} optional size={4}>
							<Input.TextArea
								placeholder='Descreva o Sotaque'
								value={handlingProgram?.accent}
								onChange={({ target: { value } }) => {
									setHandlingProgram({ ...handlingProgram, accent: value });
								}}
							/>
						</Form.Item>

						<Form.Item
							label='Características Regionais'
							style={{ marginTop: 16 }}
							optional
							size={4}>
							<Input.TextArea
								placeholder='Descreva as Características Regionais'
								value={handlingProgram?.regionalCharacteristics}
								onChange={({ target: { value } }) => {
									setHandlingProgram({ ...handlingProgram, regionalCharacteristics: value });
								}}
							/>
						</Form.Item>

						<Form.Item optional label='Regras' style={{ marginBottom: 0 }} size={4}>
							<div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
								{[
									{
										key: 'mandatory',
										label: 'Obrigatórias',
										placeholderText: 'Digite uma regra obrigatória e pressione Enter',
									},
									{
										key: 'forbidden',
										label: 'Proibidas',
										placeholderText: 'Digite uma regra proibida e pressione Enter',
									},
									{
										key: 'recommended',
										label: 'Recomendadas',
										placeholderText: 'Digite uma regra recomendada e pressione Enter',
									},
								].map(({ key, label, placeholderText }) => (
									<RuleInput
										key={key}
										ruleKey={key}
										label={label}
										placeholderText={placeholderText}
										accountInfos={handlingProgram}
										handleChangeAccountInfo={(key, value) =>
											setHandlingProgram({ ...handlingProgram, [key]: value })
										}
									/>
								))}
							</div>
						</Form.Item>

						<Form.Item label='Edição'>
							<Radio.Group
								value={handlingProgram?.isEditable}
								onChange={({ target: { value } }) =>
									setHandlingProgram({ ...handlingProgram, isEditable: value })
								}>
								<Radio value={true}>Edição permitida</Radio>
								<Radio value={false}>
									Edição <strong>não</strong> permitida
								</Radio>
							</Radio.Group>
						</Form.Item>
					</Form.Container>
				</Modal>
			</Container>
		</>
	);
};

export default ManagePrograms;
